import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { useAuth } from '../../hooks/useAuth';
import { formatDate } from '../../helpers/formatDate';
import dayjs from 'dayjs';
import LogsTable from './LogsTable';
import AuditLogsFilters from './AuditLogsFilters';
import NotFoundPage from '../NotFoundPage';
import { useDebounce } from '../../hooks/useDebounce';
import {
  fetchAuditLogsPaginated,
  selectPaginatedAuditLogs,
  selectAuditLogsLoading,
} from '../../features/auditLogs/auditLogsSlice';

import { Typography } from '@mui/material';

import styles from './style.module.css';

function AuditLogs() {
  const [inputSearch, setInputSearch] = useState('');
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [choosenModel, setChoosenModel] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [optionModel, setOptionModel] = useState([]);

  const { user } = useAuth();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const paginatedData = useSelector(selectPaginatedAuditLogs);
  const isLoading = useSelector(selectAuditLogsLoading);

  // Debounce search input to reduce API calls
  const debouncedSearch = useDebounce(inputSearch, 500);

  // Fetch audit logs data when parameters change
  useEffect(() => {
    const params = {
      page,
      take: rowsPerPage,
    };

    if (choosenModel) params.model = choosenModel;
    if (startDate) params.fromDate = startDate;
    if (endDate) params.toDate = endDate;
    if (debouncedSearch.trim()) params.keyword = debouncedSearch.trim();

    dispatch(fetchAuditLogsPaginated(params));
  }, [page, rowsPerPage, choosenModel, startDate, endDate, debouncedSearch]); // Removed dispatch from dependencies

  // Load unique models for filter dropdown (only once on mount)
  useEffect(() => {
    const fetchUniqueModels = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_BASE_URL + 'audit-logs/names', {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${Cookies.get('token') || ''}`,
          },
        });
        const models = await response.json();
        setOptionModel(models);
      } catch (error) {
        console.error('Error fetching unique models:', error);
      }
    };

    // Only fetch if we don't have models yet
    if (optionModel.length === 0) {
      fetchUniqueModels();
    }
  }, []); // Empty dependency array - run only once on mount

  const optionForModel = {
    supply: t('generic.supply'),
    user: t('generic.user'),
    'maintenance type': t('generic.maintenanceType'),
    location: t('generic.locations'),
    machineGroup: t('generic.machineGroup'),
    maintenance: t('generic.maintenance'),
    manual: t('generic.manuals'),
    machine: t('generic.machines'),
    validationDocument: t('machineList.validationDocuments'),
  };

  // Prepare data from paginated response
  const preparedData =
    paginatedData?.data?.map((auditLog) => ({
      number: auditLog.id,
      title: auditLog.title,
      description: auditLog.description,
      modifiedBy: auditLog.modified_by,
      model: auditLog.specific_model,
      created: formatDate(auditLog.created_at),
      created_at: auditLog.created_at,
      editor_description: auditLog.editor_description,
    })) || [];

  const titles = [
    t('generic.number'),
    t('generic.title'),
    t('generic.description'),
    t('generic.modifiedBy'),
    t('generic.model'),
    t('generic.createdAt'),
  ];

  const handleStartDateChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY-MM-DD');
    setStartDate(formattedDate);
    setPage(1); // Reset to first page when filter changes
  };

  const handleEndDateChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY-MM-DD');
    setEndDate(formattedDate);
    setPage(1); // Reset to first page when filter changes
  };

  const handleInputSearch = (e) => {
    setInputSearch(e.target.value);
    setPage(1); // Reset to first page when search changes
  };

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    setChoosenModel(selectedValue);
    setPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage + 1); // MUI pagination is 0-based, API is 1-based
    if (newRowsPerPage !== rowsPerPage) {
      setRowsPerPage(newRowsPerPage);
      setPage(1); // Reset to first page when page size changes
    }
  };

  const handleResetFilters = () => {
    setInputSearch('');
    setStartDate(null);
    setEndDate(null);
    setChoosenModel('');
    setPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = inputSearch.trim() !== '' || startDate || endDate || choosenModel !== '';

  if (user?.role === 'admin') {
    return (
      <section className={styles.container}>
        <div className={styles.header}>
          <Typography variant="h5" fontWeight="bold">
            {t('generic.auditLogs')}
          </Typography>
          <Typography>{t('generic.auditLogsSubtitle')}</Typography>
        </div>
        <AuditLogsFilters
          onSearchChange={handleInputSearch}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onModelChange={handleChange}
          onResetFilters={handleResetFilters}
          choosenModel={choosenModel}
          optionModel={optionModel}
          optionForModel={optionForModel}
          hasActiveFilters={hasActiveFilters}
          searchValue={inputSearch}
          startDate={startDate}
          endDate={endDate}
        />
        <LogsTable
          titles={titles}
          data={preparedData}
          paginationMeta={paginatedData?.meta}
          loading={isLoading}
          onPageChange={handlePageChange}
          logsModel={choosenModel}
        />
      </section>
    );
  } else {
    return <NotFoundPage />;
  }
}

export default AuditLogs;
